import React, { useEffect, useState } from 'react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { Box, Button, Link, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography, TextField, Container, Tooltip } from '@mui/material';
import { DmProject, getDmProjects, createDmProject, deleteDmProject } from '../api-services/dm-projects.client';
import { mapDmProjectStatusToDealStatus } from '../utils/constants';
import { useToast } from '../context/toast-context';
import useConfirm from '../hooks/useConfirm';

const statusColors: Record<string, string> = {
    'DRAFT': '#D3D3D3',
    'DEAL_ROOM_READY': '#9C27B0',
    'IN_DEAL_REVIEW': '#FFA500',
    'UNDERWRITING_PASS': '#008000',
    'UNDERWRITING_FAIL': '#FF0000',
};

const ListProjectsPage: React.FC = () => {
    const [assignOpen, setAssignOpen] = useState(false);
    const [newProjectName, setNewProjectName] = useState('');
    const [projects, setProjects] = useState<DmProject[]>([]);
    const [creating, setCreating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { showToast } = useToast();
    const { confirmContainer, onConfirmMessage } = useConfirm();

    useEffect(() => {
        getDmProjects().then(setProjects);
    }, []);

    const handleCreateProject = async () => {
        if (!newProjectName.trim()) return;
        setCreating(true);
        try {
            const newProject = await createDmProject(newProjectName.trim());
            setProjects((prev) => [...prev, newProject]);
            setNewProjectName('');
            setAssignOpen(false);
            showToast({
                message: 'Project created successfully',
                severity: 'success',
            })
        } catch (err) {
            showToast({
                message: 'Failed to create project.',
                severity: 'error',
            })
        } finally {
            setCreating(false);
        }
    };

    const handleViewProject = (id: string) => {
        window.location.href = `deal-management/projects/${id}`;
    };

    const handleDeleteProject = async (projectToDelete: DmProject) => {
        const numFiles = projectToDelete.files?.length || 0;
        const hasReport = !!projectToDelete.report;


        const confirm = await onConfirmMessage({
            title: 'Delete Project',
            message: (
                <>
                    Are you sure you want to delete the project <b>"{projectToDelete.name}"</b>?<br/>
                    <br/>
                    <b>Warning:</b> When project is deleted, <b>{numFiles} file{numFiles !== 1 ? 's' : ''}</b> {numFiles === 1 ? 'is' : 'are'} going to be deleted{hasReport ? <>, <b>report</b> is going to be deleted as well.</> : '.'}
                </>
            ),
            textConfirm: 'Delete',
        });
        if (!confirm) return;
        setDeleting(true);
        try {
            await deleteDmProject(projectToDelete.id);

            setProjects((prev) => prev.filter(p => p.id !== projectToDelete.id));
            showToast({
                message: 'Project deleted successfully',
                severity: 'success',
            });
        } catch (err) {
            showToast({
                message: 'Failed to delete project.',
                severity: 'error',
            });
        } finally {
            setDeleting(false);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: 'PROJECT NAME',
            flex: 1,
            renderCell: (params) => (
                <Link href={`deal-management/projects/${params.row.id}`} underline="hover" sx={{ color: '#9C27B0', fontWeight: 500 }}>
                    {params.value}
                </Link>
            ),
        },
        {
            field: 'id',
            headerName: 'PROJECT ID',
            flex: 1,
        },
        {
            field: 'status',
            headerName: 'DEAL STATUS',
            flex: 1.5,
            renderCell: (params) => (
                <Box
                    sx={{
                        background: statusColors[params.value] || '#eee',
                        color: '#fff',
                        borderRadius: '20px',
                        px: 2,
                        py: 0.5,
                        fontWeight: 500,
                        display: 'inline-block',
                        minWidth: 120,
                        textAlign: 'center',
                    }}
                >
                    {mapDmProjectStatusToDealStatus(params.value)}
                </Box>
            ),
        },
        {
            field: 'actions',
            headerName: 'ACTIONS',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <IconButton onClick={() => handleViewProject(params.row.id)}>
                        <Tooltip title="View project">
                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.5 4C5.5 4 2.5 10 2.5 10C2.5 10 5.5 16 10.5 16C15.5 16 18.5 10 18.5 10C18.5 10 15.5 4 10.5 4ZM10.5 14C8.01472 14 6 12.2091 6 10C6 7.79086 8.01472 6 10.5 6C12.9853 6 15 7.79086 15 10C15 12.2091 12.9853 14 10.5 14ZM10.5 8C9.11929 8 8 9.11929 8 10.5C8 11.8807 9.11929 13 10.5 13C11.8807 13 13 11.8807 13 10.5C13 9.11929 11.8807 8 10.5 8Z" fill="#9C27B0"/>
                            </svg>
                        </Tooltip>
                    </IconButton>
                    <IconButton onClick={() => handleDeleteProject(params.row)}>
                        <Tooltip title="Delete project">
                            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.33333 15.8333C5.33333 16.75 6.08333 17.5 7 17.5H13.6667C14.5833 17.5 15.3333 16.75 15.3333 15.8333V5.83333H5.33333V15.8333ZM16.1667 3.33333H13.25L12.4167 2.5H8.25L7.41667 3.33333H4.5V5H16.1667V3.33333Z" fill="#D32F2F" />
                            </svg>
                        </Tooltip>
                    </IconButton>
                </Box>
            ),
        },
    ];

    return (
        <Container>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box component="h1" sx={{ fontSize: 36, fontWeight: 700, mb: 0 }}>
                        Available Projects
                    </Box>
                    <Button
                        variant="contained"
                        sx={{ background: '#6435C9', borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 16, '&:hover': { background: '#5127B0' } }}
                        onClick={() => setAssignOpen(true)}
                    >
                        Add Project
                    </Button>
                </Box>
                <Box sx={{ background: '#fff', borderRadius: 2, boxShadow: 1, p: 2 }}>
                    <DataGridPro
                        rows={projects}
                        columns={columns}
                        autoHeight
                        hideFooter
                        disableColumnMenu
                        disableColumnFilter
                        disableColumnSelector
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                background: '#fff',
                                fontWeight: 700,
                                fontSize: 16,
                                borderBottom: '2px solid #eee',
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: 16,
                                borderBottom: '1px solid #eee',
                            },
                            '& .MuiDataGrid-row': {
                                minHeight: 60,
                                maxHeight: 60,
                            },
                            '& .MuiDataGrid-virtualScroller': {
                                minHeight: 400,
                            },
                        }}
                        loading={deleting || creating}
                    />
                </Box>

                {/* Add Project Modal */}
                <Dialog
                    open={assignOpen}
                    onClose={() => setAssignOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: { borderRadius: 4, p: 0, overflow: 'visible' }
                    }}
                >
                    <DialogTitle sx={{ fontSize: 28, fontWeight: 700, pb: 0, pr: 5 }}>
                        Add Project
                        <IconButton
                            aria-label="close"
                            onClick={() => setAssignOpen(false)}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: '#888',
                            }}
                        >
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M27 14.41L25.59 13L20 18.59L14.41 13L13 14.41L18.59 20L13 25.59L14.41 27L20 21.41L25.59 27L27 25.59L21.41 20L27 14.41Z" fill="black" fill-opacity="0.56" />
                            </svg>
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 3, pb: 2, px: 4 }}>
                        <Typography sx={{ fontWeight: 500, fontSize: 18, mb: 1.5 }}>
                            Project Name
                        </Typography>
                        <TextField
                            fullWidth
                            placeholder="Enter new project name..."
                            variant="outlined"
                            value={newProjectName}
                            onChange={e => setNewProjectName(e.target.value)}
                            sx={{
                                background: '#FAFBFC',
                                borderRadius: 2,
                                mt: 1,
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    padding: '2px 8px',
                                    minHeight: 56,
                                    alignItems: 'center',
                                },
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && newProjectName.trim() && !creating) {
                                    handleCreateProject();
                                }
                            }}
                            disabled={creating || deleting}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 4, pb: 3, pt: 2, justifyContent: 'flex-end' }}>
                        <Button
                            onClick={() => setAssignOpen(false)}
                            sx={{
                                background: '#000',
                                color: '#fff',
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: 16,
                                px: 4,
                                mr: 2,
                                '&:hover': { background: '#222' },
                            }}
                            disabled={creating || deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            sx={{
                                background: '#6435C9',
                                color: '#fff',
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: 16,
                                px: 4,
                                '&:hover': { background: '#5127B0' },
                            }}
                            onClick={handleCreateProject}
                            disabled={!newProjectName.trim() || creating}
                        >
                            {creating ? 'Creating...' : 'Confirm'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {confirmContainer}
            </Box>
        </Container>
    );
};

export default ListProjectsPage; 